import React from "react";
import { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";
import { Link } from "react-router-dom";

import { DateTime } from "luxon";

import { apiGet, V2 } from "../../Api";
import {
	formatDateLocalDayOfWeek,
	formatDateTimeLocal,
	formatName,
	formatTimeLocal,
} from "../../Utilities";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import HasRight from "../HasRight";
import ViewToolbar from "../ViewToolbar";
import withAuth from "../withAuth";
import withTitle, { IViewTitleProps } from "../withTitle";

import "../../arrow.css";
import "../../blocks.css";
import "../../button.css";
import "../../schedule.css";
import "../viewer.css";
import withProps from "../withProps";

interface IAppointmentCalendarDayViewState {
	date: DateTime;
	events: { [time: string]: any[] };
	isLoading: boolean | string;

	locations: any[];
	people: any[];
	appointmentTypes: any[];
	searchDateType: string;
	searchLocation: string;
	searchPerson: string;
	searchType: string;
	searchStatus: string;
	searchMode: string;

	[name: string]: any;
}

interface IAppointmentCalendarDayViewProps
	extends RouteComponentPropsHistory<any, any>,
		IViewTitleProps,
		IAuthProps {
	mode: "all" | "hf";
}

class AppointmentCalendarDayView extends React.Component<
	IAppointmentCalendarDayViewProps,
	IAppointmentCalendarDayViewState
> {
	constructor(props: Readonly<IAppointmentCalendarDayViewProps>) {
		super(props);

		const newDate = DateTime.fromISO(this.props.match.params.date);

		let searchPerson = this.props.location.state ? this.props.location.state.searchPerson : "";
		if (searchPerson === "" && !props.authContext.hasRight("appt:readall")) {
			searchPerson = "caller";
		}

		let defaultType = "";
		if (props.authContext.isInGroup(WellKnownGroups.UroTechnician)) {
			defaultType = "uro";
		} else if (props.authContext.isInGroup(WellKnownGroups.PelletTechnician)) {
			defaultType = "pellet";
		}

		this.state = {
			date: newDate,
			events: {},
			isLoading: true,

			locations: [],
			people: [],
			appointmentTypes: [],
			searchDateType: (props.location.state ? props.location.state.searchDateType : "for") || "for",
			searchLocation: props.location.state ? props.location.state.searchLocation : "",
			searchPerson,
			searchType: props.location.state ? props.location.state.searchType : defaultType,
			searchStatus: props.location.state ? props.location.state.searchStatus : "",
			searchMode: (props.location.state && props.location.state.searchMode) || props.mode,
		};

		this.prevDay = this.prevDay.bind(this);
		this.nextDay = this.nextDay.bind(this);
		this.handleElementChange = this.handleElementChange.bind(this);
	}

	public async componentDidMount() {
		try {
			const [locations, appointmentTypes] = await Promise.all([
				apiGet<any[]>("locations"),
				V2.get<any[]>("appointments/types"),
			]);
			const marketers = this.props.authContext.hasRight("appt:readall")
				? await apiGet<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`)
				: [];

			this.setState(
				{
					locations,
					people: marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name))),
					appointmentTypes,
				},
				() => this.requestEvents()
			);
		} catch (e) {
			this.setState({
				isLoading: e.message,
			});
		}
	}

	public render() {
		let content;

		if (typeof this.state.isLoading === "string") {
			content = <div className="error-text">{this.state.isLoading}</div>;
		} else if (this.state.isLoading) {
			content = <div className="lds-dual-ring spinner-center" />;
		} else if (Object.keys(this.state.events).length === 0) {
			content = <span>No appointments found</span>;
		} else {
			content = Object.keys(this.state.events).map((k) => (
				<section
					key={k}
					className="schedule-section">
					<h3>
						{this.state.searchDateType === "for"
							? formatTimeLocal(
									this.state.date.set({
										hour: Math.floor(parseInt(k, 10) / 60),
										minute: Math.floor(parseInt(k, 10) % 60),
									})
							  )
							: k}
					</h3>
					<ul>
						{this.state.events[k].map((e: any) => (
							<li
								key={e.id}
								className={this.statusToClass(e.status)}>
								<Link to={`/appointments/${e.id}`}>
									<span>{formatName(e.patient.name)}</span>
									<span className="small-text">
										{e.appointmentType.name} |{" "}
										{this.state.searchDateType === "for"
											? `${e.location.name}`
											: formatDateTimeLocal(e.appointmentTimestamp)}
										{e.isFromFair ? (
											<span>
												{" "}
												| <b>HF</b>
											</span>
										) : null}
									</span>
									{this.state.searchDateType === "on" && e.employee ? (
										<span className="medium-text">{formatName(e.employee.name)}</span>
									) : (
										""
									)}
									{/*span className="small-text member-list">{e.members.map((m: any) => formatName(m.name)).join(", ")}</span>*/}
								</Link>
							</li>
						))}
					</ul>
				</section>
			));
		}

		return (
			<div className="viewer-root">
				<ViewToolbar>
					<HasRight right="appt:create-disabled">
						<Link
							to={{ pathname: "/appointments/create", state: { date: this.state.date } }}
							className="icon-plus"
						/>
					</HasRight>
				</ViewToolbar>
				<div className="filter-control-box">
					<div className="control-group">
						<button
							className="button btn-light arrow arrow-left arrow-box"
							onClick={this.prevDay}
						/>
						<button
							className="button btn-light arrow arrow-right arrow-box"
							onClick={this.nextDay}
						/>
						<span>{formatDateLocalDayOfWeek(this.state.date)}</span>
					</div>
					<select
						className="push-right-if-wide control-max-150px"
						value={this.state.searchStatus}
						name="searchStatus"
						onChange={this.handleElementChange}
						disabled={this.state.isLoading === true}>
						<option value="">All Status</option>
						<option value="pending">Pending</option>
						<option value="pendingreschedule">Pending Reschedule</option>
						<option value="active">Active</option>
						<option value="complete">Complete</option>
						<option value="noshow">No Show</option>
						<option value="canceled">Canceled</option>
					</select>
					<select
						className="control-max-150px"
						value={this.state.searchType}
						name="searchType"
						onChange={this.handleElementChange}
						disabled={this.state.isLoading === true}>
						<option value="">All Types</option>
						{this.state.appointmentTypes.map((a) => (
							<option
								key={a.id}
								value={a.type}>
								{a.name}
							</option>
						))}
					</select>
					<select
						value={this.state.searchDateType}
						name="searchDateType"
						onChange={this.handleElementChange}
						disabled={this.state.isLoading === true}>
						<option value="for">Scheduled For</option>
						<option value="on">Created On</option>
					</select>
					<select
						value={this.state.searchLocation}
						name="searchLocation"
						onChange={this.handleElementChange}
						disabled={this.state.isLoading === true}>
						<option value="">All Locations</option>
						{this.state.locations.map((a) => (
							<option
								key={a.id}
								value={a.id}>
								{a.shortName || a.name}
							</option>
						))}
					</select>
					<select
						className="control-max-150px"
						value={this.state.searchPerson}
						name="searchPerson"
						onChange={this.handleElementChange}
						disabled={this.state.isLoading === true}>
						<HasRight right="appt:readall">
							<option value="">Everyone</option>
						</HasRight>
						<option value="caller">You</option>
						<HasRight right="appt:readall">
							{this.state.people.map((a) => (
								<option
									key={a.id}
									value={a.id}>
									{formatName(a.name)}
								</option>
							))}
						</HasRight>
					</select>
				</div>
				<div className="info-block">
					<header>
						<h3>
							{this.state.isLoading ? "Loading..." : this.personToString(this.state.searchPerson)}
						</h3>
					</header>
					{content}
				</div>
			</div>
		);
	}

	private async requestEvents() {
		try {
			this.setState({
				isLoading: true,
			});

			const start = this.state.date.startOf("day");
			const end = start.plus({ day: 1 });

			const parameters = [
				"limit=200",
				`after=${encodeURIComponent(start.toISO())}`,
				`before=${encodeURIComponent(end.toISO())}`,
				`date_type=${this.state.searchDateType}`,
				`kind=${this.state.searchMode}`,
			];
			if (this.state.searchPerson) {
				parameters.push(`person=${this.state.searchPerson}`);
			}
			if (this.state.searchLocation) {
				parameters.push(`location=${this.state.searchLocation}`);
			}
			if (this.state.searchType) {
				parameters.push(`type=${this.state.searchType}`);
			}
			if (this.state.searchStatus) {
				parameters.push(`status=${this.state.searchStatus}`);
			}

			const json = await apiGet<any>(`appointments/summary?${parameters.join("&")}`);

			const today = DateTime.local();
			if (start.hasSame(today, "day")) {
				this.props.viewTitleContext.changeTitle(
					this.props.mode === "hf"
						? "Today's HF Appointment Calendar"
						: "Today's Appointment Calendar"
				);
			} else {
				this.props.viewTitleContext.changeTitle(
					this.props.mode === "hf" ? "HF Appointment Calendar" : "Appointment Calendar"
				);
			}
			this.props.viewTitleContext.changeTitle(
				`Appointments - ${formatDateLocalDayOfWeek(this.state.date)}`,
				"window"
			);

			const map: { [time: string]: any[] } = {};

			if (this.state.searchDateType === "for") {
				for (const event of json) {
					event.appointmentTimestamp = DateTime.fromISO(event.appointmentTimestamp).toLocal();
					const timeBin = event.appointmentTimestamp.hour * 60 + event.appointmentTimestamp.minute;
					if (!map[timeBin]) {
						map[timeBin] = [];
					}
					map[timeBin].push(event);
				}
			} else {
				for (const event of json) {
					event.appointmentTimestamp = DateTime.fromISO(event.appointmentTimestamp).toLocal();
					const bin = event.location.name;
					if (!map[bin]) {
						map[bin] = [];
					}
					map[bin].push(event);
				}
			}

			this.setState({
				events: map,
				isLoading: false,
			});
		} catch (e) {
			// Request failed
			this.setState({
				isLoading: "Failed to load schedule",
			});
		}
	}

	private statusToClass(status: number) {
		switch (status) {
			case 0:
				return "";
			case 1:
				return "";
			case 5:
				return "";

			case 2:
				return "appt-bad";
			case 3:
				return "appt-noshow";
			case 4:
				return "appt-good";
			default:
				return "";
		}
	}

	private prevDay() {
		// Yesterday
		this.gotoDate(this.state.date.minus({ day: 1 }));
	}

	private nextDay() {
		// Tomorrow
		this.gotoDate(this.state.date.plus({ day: 1 }));
	}

	private gotoDate(date: DateTime) {
		this.setState(
			(s) => {
				// Change date while keeping search state
				const currentLocation = this.props.location;
				currentLocation.pathname =
					this.props.mode === "hf"
						? `/appointments/calendar/hf/${date.toISODate()}`
						: `/appointments/calendar/${date.toISODate()}`;
				this.props.history.replace(currentLocation);

				return {
					date,
				};
			},
			() => this.requestEvents()
		);
	}

	private saveSearchState() {
		// Stash the currently displayed month so the user can come back to it
		const currentLocation = this.props.location;
		currentLocation.state = {
			searchDateType: this.state.searchDateType,
			searchLocation: this.state.searchLocation,
			searchPerson: this.state.searchPerson,
			searchType: this.state.searchType,
			searchMode: this.state.searchMode,
		};
		this.props.history.replace(currentLocation);
	}

	private handleElementChange(e: ChangeEvent<HTMLSelectElement>) {
		const value = e.currentTarget.value;
		const name = e.currentTarget.name;

		this.setState(
			{
				[name]: value,
				events: [],
			},
			() => {
				this.saveSearchState();
				this.requestEvents();
			}
		);
	}

	private personToString(person: string) {
		switch (person) {
			case "":
				return "Everyone";
			case "caller":
				return "You";
			default:
				const per = this.state.people.find((a) => a.id === person);
				return per ? formatName(per.name) : person;
		}
	}
}

export default withAuth(
	withTitle("Appointment Calendar", withProps(AppointmentCalendarDayView, { mode: "all" }))
);
export const AppointmentHfCalendarDayView = withAuth(
	withTitle("HF Appointment Calendar", withProps(AppointmentCalendarDayView, { mode: "hf" }))
);
